import apiClient from '@/lib/api-client';

interface ApiEnvelope<T> {
  data: T;
}

export interface CustomerBookingOrganization {
  id: number;
  name: string;
  slug: string | null;
  timezone: string;
  currency: string;
  phone: string | null;
  address: string | null;
}

/** Present only when the booking sits on a shared slot (group class). */
export interface CustomerBookingGroup {
  capacity: number;
  booked: number;
  spots_remaining: number;
}

export interface CustomerBooking {
  id: number;
  public_id: string;
  booking_code: string | null;
  guest_token: string;
  status: string;
  start_time: string | null;
  end_time: string | null;
  cancelled_at: string | null;
  cancellation_reason: string | null;
  can_cancel: boolean;
  can_reschedule: boolean;
  hide_price: boolean;
  customer_full_name: string;
  group: CustomerBookingGroup | null;
  service: {
    id: number | null;
    name: string | null;
    duration_minutes: number | null;
    price: number | null;
  };
  provider: {
    id: number | null;
    display_name: string | null;
  };
  organization: CustomerBookingOrganization;
}

/** A spot the customer is queueing for on a full group class. */
export interface CustomerWaitlistEntry {
  id: number;
  guest_token: string;
  status: string;
  position: number | null;
  start_time: string | null;
  end_time: string | null;
  capacity: number;
  hide_price: boolean;
  service: {
    id: number | null;
    name: string | null;
    duration_minutes: number | null;
    price: number | null;
  };
  provider: {
    id: number | null;
    display_name: string | null;
  };
  organization: CustomerBookingOrganization;
}

export interface CustomerBookingsResponse {
  email: string;
  expires_at: string;
  upcoming_bookings: CustomerBooking[];
  past_bookings: CustomerBooking[];
  waitlist_entries: CustomerWaitlistEntry[];
}

class CustomerBookingsService {
  /**
   * Requests a magic link. The API always answers the same way, whether or not
   * the email has bookings, so nothing here reveals customer existence.
   */
  async requestMagicLink(email: string): Promise<{ message: string }> {
    const { data } = await apiClient.post<ApiEnvelope<{ message: string }>>(
      '/v1/guest/customer_bookings/magic_link',
      { email },
    );
    return data.data;
  }

  /** Revokes the magic link server-side, so the URL is dead once the user logs out. */
  async logout(token: string): Promise<{ message: string }> {
    const { data } = await apiClient.delete<ApiEnvelope<{ message: string }>>(
      `/v1/guest/customer_bookings/${token}`,
    );
    return data.data;
  }

  async getByToken(token: string): Promise<CustomerBookingsResponse> {
    const { data } = await apiClient.get<ApiEnvelope<CustomerBookingsResponse>>(
      `/v1/guest/customer_bookings/${token}`,
    );
    return data.data;
  }
}

export const customerBookingsService = new CustomerBookingsService();
